import { CardTitle } from "@/modern/components/ui/card";
import { DashboardCard } from "@/modern/components/dashboard-card";
import { Button } from "@/modern/components/ui/button-shadcn";
import { ButtonGroup } from "@/modern/components/ui/button-group";
import { ArrowLeft, Expand, Loader2, PlusIcon } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/modern/components/ui/tooltip";
import { ChecklistList } from "./components/ChecklistList";
import { EmptyChecklistsState } from "./components/EmptyChecklistsState";
import { ChecklistProgress } from "./components/ChecklistProgress";
import { ChecklistItemList } from "./components/ChecklistItemList";
import { ChecklistAssignedAvatars } from "@/modern/components/board-user-select-field";
import { cn } from "@/modern/utils";
import { EntityChecklistProvider, useEntityChecklistContext } from "./context/EntityChecklistContext";
import type { EntityWidgetProps } from "@/other/constants/EntityManagerConfig";

export const EntityChecklistContent = ({ onClose, headerTitle }: { onClose?: () => void; headerTitle?: string }) => {
  const {
    selectedChecklist,
    handleHideChecklistDetails,
    handleShowChecklistDetails,
    handleOpenEditor,
    hasChecklists,
    checklists,
    selectedChecklistItems,
    canEditSelectedChecklist,
    isLoadingChecklists,
    isLoadingChecklist
  } = useEntityChecklistContext();

  const entityPrefix = headerTitle?.includes(" | ")
    ? headerTitle.slice(0, headerTitle.indexOf(" | "))
    : undefined;
  const label = selectedChecklist?.title || "Checklists";
  const title = entityPrefix ? `${entityPrefix} | ${label}` : (headerTitle ?? label);
  const isDetailLoading = Boolean(selectedChecklist && isLoadingChecklist);

  return (
    <DashboardCard
      onClose={onClose}
      cardContentClassName="h-full flex flex-col p-3"
      header={
        <div className="flex items-center justify-between w-full">
          <CardTitle className={cn("text-base gap-3 flex items-center truncate min-w-0", selectedChecklist?.archived && "line-through")}>
            {selectedChecklist && (
              <Button variant="ghost" size="icon" className="size-7 p-0 text-muted-foreground" onClick={() => handleHideChecklistDetails()}>
                <ArrowLeft className="size-4" />
              </Button>
            )}
            <span className="truncate min-w-0 pr-2">{title}</span>
          </CardTitle>
          <div className="flex items-center gap-2 shrink-0">
            {selectedChecklist ? <ChecklistAssignedAvatars checklist={selectedChecklist} max={3} /> : null}
            <ButtonGroup>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="entityHeader"
                    className={cn(selectedChecklist && "w-auto px-3")}
                    size="icon"
                    onClick={() => handleOpenEditor(!selectedChecklist)}
                    disabled={(isLoadingChecklists && !selectedChecklist) || isDetailLoading}
                  >
                    {selectedChecklist ? (
                      <>
                        <Expand className="size-4" /> {canEditSelectedChecklist && "Manage"}
                      </>
                    ) : (
                      <>
                        <PlusIcon className="size-4" />
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{selectedChecklist ? `${canEditSelectedChecklist ? "Edit" : "View"} checklist` : "Add checklist"}</TooltipContent>
              </Tooltip>
            </ButtonGroup>
          </div>
        </div>
      }
    >
      {isLoadingChecklists && !selectedChecklist && !hasChecklists ? (
        <div className="flex flex-1 items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          Loading checklists…
        </div>
      ) : hasChecklists ? (
        <>
          {selectedChecklist ? (
            isDetailLoading ? (
              <div className="flex flex-1 items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" aria-hidden />
                Loading checklist…
              </div>
            ) : (
              <div className="flex flex-col gap-4 pt-1">
                <ChecklistProgress
                  checklist={selectedChecklist}
                  checklistItems={selectedChecklistItems}
                />
                <ChecklistItemList
                  checklistItems={selectedChecklistItems}
                  uiConfig={{
                    showImageHandler: true,
                    showComment: true,
                    showButtonsOnHover: true,
                    showDeleteHandler: false,
                    showDragHandle: false,
                  }}
                />
              </div>
            )
          ) : (
            <ChecklistList
              checklists={checklists}
              onShowDetails={handleShowChecklistDetails}
            />
          )}
        </>
      ) : (
        <EmptyChecklistsState onOpenEditor={handleOpenEditor} />
      )}
    </DashboardCard>
  );
}

export const EntityChecklistView = ({ record, onClose, headerTitle }: EntityWidgetProps) => {
  return (<EntityChecklistProvider record={record}>
    <EntityChecklistContent onClose={onClose} headerTitle={headerTitle} />
  </EntityChecklistProvider>)
};
